import * as React from "react";
import type { DashboardPropertyFilters } from "../../../convex/types";
import { useDebounce } from "@/hooks/use-debounce";
import {
  DEFAULT_DASHBOARD_PROPERTY_FILTERS,
  areDashboardPropertyFiltersEqual,
  buildDashboardPropertySearchParams,
  parseDashboardPropertyFilters,
} from "./filters";

const FILTER_QUERY_KEYS = [
  "q",
  "tags",
  "types",
  "status",
  "bedMin",
  "bedMax",
  "bathMin",
  "bathMax",
  "buildMin",
  "buildMax",
  "landMin",
  "landMax",
  "yearMin",
  "yearMax",
  "priceMin",
  "priceMax",
  "video",
  "visibility",
  "listingAgent",
];

/**
 * Keeps property dashboard filters synced with URL query params while
 * debouncing freeform and range inputs for live result updates.
 */
export function usePropertyTableFilters() {
  const [filters, setFilters] = React.useState<DashboardPropertyFilters>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_DASHBOARD_PROPERTY_FILTERS;
    }

    return parseDashboardPropertyFilters(window.location.search);
  });

  const debouncedSearchQuery = useDebounce(filters.searchQuery, 350);
  const debouncedBedroomMin = useDebounce(filters.bedroomMin, 350);
  const debouncedBedroomMax = useDebounce(filters.bedroomMax, 350);
  const debouncedBathroomMin = useDebounce(filters.bathroomMin, 350);
  const debouncedBathroomMax = useDebounce(filters.bathroomMax, 350);
  const debouncedBuildSizeMin = useDebounce(filters.buildSizeMin, 350);
  const debouncedBuildSizeMax = useDebounce(filters.buildSizeMax, 350);
  const debouncedLandSizeMin = useDebounce(filters.landSizeMin, 350);
  const debouncedLandSizeMax = useDebounce(filters.landSizeMax, 350);
  const debouncedYearBuiltMin = useDebounce(filters.yearBuiltMin, 350);
  const debouncedYearBuiltMax = useDebounce(filters.yearBuiltMax, 350);
  const debouncedPriceMin = useDebounce(filters.priceMin, 350);
  const debouncedPriceMax = useDebounce(filters.priceMax, 350);

  const appliedFilters = React.useMemo<DashboardPropertyFilters>(() => {
    return {
      ...filters,
      searchQuery: debouncedSearchQuery,
      bedroomMin: debouncedBedroomMin,
      bedroomMax: debouncedBedroomMax,
      bathroomMin: debouncedBathroomMin,
      bathroomMax: debouncedBathroomMax,
      buildSizeMin: debouncedBuildSizeMin,
      buildSizeMax: debouncedBuildSizeMax,
      landSizeMin: debouncedLandSizeMin,
      landSizeMax: debouncedLandSizeMax,
      yearBuiltMin: debouncedYearBuiltMin,
      yearBuiltMax: debouncedYearBuiltMax,
      priceMin: debouncedPriceMin,
      priceMax: debouncedPriceMax,
    };
  }, [
    filters,
    debouncedBathroomMax,
    debouncedBathroomMin,
    debouncedBedroomMax,
    debouncedBedroomMin,
    debouncedBuildSizeMax,
    debouncedBuildSizeMin,
    debouncedLandSizeMax,
    debouncedLandSizeMin,
    debouncedPriceMax,
    debouncedPriceMin,
    debouncedSearchQuery,
    debouncedYearBuiltMax,
    debouncedYearBuiltMin,
  ]);

  React.useEffect(() => {
    const handlePopState = () => {
      const nextFilters = parseDashboardPropertyFilters(window.location.search);

      setFilters((currentFilters) => {
        if (areDashboardPropertyFiltersEqual(currentFilters, nextFilters)) {
          return currentFilters;
        }

        return nextFilters;
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const currentParams = new URLSearchParams(window.location.search);
    for (const key of FILTER_QUERY_KEYS) {
      currentParams.delete(key);
    }

    const filterParams = buildDashboardPropertySearchParams(filters);
    filterParams.forEach((value, key) => {
      currentParams.set(key, value);
    });

    const nextQueryString = currentParams.toString();
    const nextUrl =
      nextQueryString === ""
        ? window.location.pathname
        : `${window.location.pathname}?${nextQueryString}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, "", nextUrl);
    }
  }, [filters]);

  const resetFilters = React.useCallback(() => {
    setFilters(DEFAULT_DASHBOARD_PROPERTY_FILTERS);
  }, []);

  return {
    filters,
    appliedFilters,
    setFilters,
    resetFilters,
  };
}
